import { lazy, ReactNode, Suspense, useCallback, useEffect, useMemo, useState } from 'react'
import { AppShortcutsContext, IShortcutListSection } from '../../contexts/AppShortcutsContext'
import { isEditableElementFocused } from '../../helpers/helpers'
import {
  AppShortcut,
  buildAppShortcutSection,
  buildShortcutSheet,
  matchAppShortcut,
} from './appShortcuts'

// Opened by shortcut; the new-task form carries the date picker, so both load on first use.
const KeyboardShortcutsModal = lazy(() => import('./KeyboardShortcutsModal'))
const NewTaskModal = lazy(() => import('./NewTaskModal'))

interface IProps {
  /** Off without app chrome (snapshots, module frames) and for roles with neither tasks nor AIM. */
  isEnabled: boolean
  /** The AIM bubble is mounted, so ⌘⇧K and ⌘J work from every page. */
  hasAimBubble: boolean
  canCreateTask: boolean
  children: ReactNode
}

/** Drops one registration, not every equal one: two mounts can register the same constant. */
const withoutFirst = <T,>(list: T[], item: T): T[] => {
  const index = list.indexOf(item)
  return index === -1 ? list : [...list.slice(0, index), ...list.slice(index + 1)]
}

/**
 * The shortcuts that work on every page: N for a New Task pop-up and ? or ⌘/ for
 * the one shortcut sheet, which lists these plus whatever the mounted surfaces
 * register through `usePageShortcuts`. AIM's chords live in MessagesBubble.
 */
const AppShortcutsProvider = ({ isEnabled, hasAimBubble, canCreateTask, children }: IProps) => {
  const [isShortcutListOpen, setIsShortcutListOpen] = useState(false)
  const [isNewTaskOpen, setIsNewTaskOpen] = useState(false)
  const [pageSections, setPageSections] = useState<IShortcutListSection[]>([])
  const [newTaskCompanyIds, setNewTaskCompanyIds] = useState<string[]>([])

  // Blurs a focused control first: the Modal hands focus back to the trigger on close,
  // and a focused button is what ACT's list shortcuts stand down for. A field keeps
  // its caret, since ⌘/ is pressed while typing.
  const openShortcutList = useCallback(() => {
    if (document.activeElement instanceof HTMLElement && !isEditableElementFocused()) {
      document.activeElement.blur()
    }
    setIsShortcutListOpen(true)
  }, [])
  const closeShortcutList = useCallback(() => setIsShortcutListOpen(false), [])
  const closeNewTask = useCallback(() => setIsNewTaskOpen(false), [])

  const registerPageShortcuts = useCallback((section: IShortcutListSection) => {
    setPageSections((sections) => [...sections, section])
    return () => setPageSections((sections) => withoutFirst(sections, section))
  }, [])

  const registerNewTaskCompany = useCallback((companyId: string) => {
    setNewTaskCompanyIds((ids) => [...ids, companyId])
    return () => setNewTaskCompanyIds((ids) => withoutFirst(ids, companyId))
  }, [])

  useEffect(() => {
    if (!isEnabled) return
    const handleKeyDown = (event: KeyboardEvent) => {
      const shortcut = matchAppShortcut(event)
      if (shortcut === AppShortcut.ShortcutList) {
        event.preventDefault()
        openShortcutList()
      } else if (shortcut === AppShortcut.NewTask && canCreateTask) {
        event.preventDefault()
        setIsNewTaskOpen(true)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isEnabled, canCreateTask, openShortcutList])

  const sheet = useMemo(
    () =>
      buildShortcutSheet(
        buildAppShortcutSection({ hasAimBubble, canCreateTask }),
        // One section per surface, however many times it registered.
        [...new Set(pageSections)],
      ),
    [hasAimBubble, canCreateTask, pageSections],
  )

  const contextValue = useMemo(
    () => ({ openShortcutList, registerPageShortcuts, registerNewTaskCompany }),
    [openShortcutList, registerPageShortcuts, registerNewTaskCompany],
  )

  return (
    <AppShortcutsContext.Provider value={contextValue}>
      {children}
      <Suspense fallback={null}>
        {isShortcutListOpen && (
          <KeyboardShortcutsModal sections={sheet} onClose={closeShortcutList} />
        )}
        {isNewTaskOpen && (
          <NewTaskModal companyId={newTaskCompanyIds.at(-1) ?? null} onClose={closeNewTask} />
        )}
      </Suspense>
    </AppShortcutsContext.Provider>
  )
}

export default AppShortcutsProvider
